import API from "./api";
import RESOURCE_API from "./respourceApi";
import { AI } from "./endpointMap";

function parseErrorMessage(error) {
  try {
    // FastAPI error format: { detail: "message" } or { detail: [...] }
    if (error?.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail) && detail.length > 0) {
        return detail[0]?.msg || JSON.stringify(detail[0]);
      }
      if (detail?.message) return detail.message;
      return JSON.stringify(detail);
    }
    // Legacy Frappe error format (kept during migration)
    const errorArray = JSON.parse(
      error?.response?.data?._server_messages || error?._server_messages
    );

    if (Array.isArray(errorArray) && errorArray.length > 0) {
      const firstErrorObj = JSON.parse(errorArray[0]);

      if (firstErrorObj && firstErrorObj.message) {
        return firstErrorObj.message;
      }
    }

    return error?.response?.data?.message;
  } catch (e) {
    return error?.response?.data?.message || error?.response?.data?.detail || "Error Posting Data";
  }
}

// GET Request
export const fetchData = async (endpoint, params = {}) => {
  try {
    const response = await API.get(endpoint, { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error fetching data";
  }
};

export const forgotPassword = async (endpoint, params = {}) => {
  try {
    const response = await API.put(endpoint, params);
    return response.data;
  } catch (error) {
    throw parseErrorMessage(error) || "Error resetting password";
  }
};

export const postData = async (endpoint, data) => {
  try {
    const response = await API.post(endpoint, data);

    return response.data;
  } catch (error) {
    if (error?.response?.data?._server_messages) {
      console.log("PARSED ERROR", parseErrorMessage(error?.response?.data));
      throw parseErrorMessage(error?.response?.data);
    }
    throw parseErrorMessage(error) ||
      error?.response?.data?.message?.message ||
      error?.response?.data?.message ||
      "Error posting data";
  }
};

// Like postData but preserves the FULL axios error so callers can
// inspect `err.response.status` + `err.response.data` for structured
// non-2xx payloads. Used by the GST single-fetch path so the
// Whitebooks 409 (with gst_username / gstin / client_id fields) can
// drive the OTP inline retry modal — standard postData stringifies
// the error and loses those fields. ONLY use this when the caller
// genuinely needs to branch on the response payload; everywhere else
// keep using postData so the global error toast still works.
export const postDataRaw = async (endpoint, data) => {
  const response = await API.post(endpoint, data);
  return response.data;
};

export const postResponseData = async (endpoint, data) => {
  try {
    const response = await API.post(endpoint, data);

    return response.data;
  } catch (error) {
    // No `response` at all means the request NEVER REACHED the server -
    // DNS failure, dropped connection, VPN/captive-portal, offline. The old
    // `throw error?.response?.data` threw `undefined` here, so every caller's
    // catch fell through to its generic "failed" toast and a pure client-side
    // network blip read as a product failure (seen live: ERR_NAME_NOT_RESOLVED
    // on a generate call, while the backend logged no request at all).
    // A typed marker lets callers say what actually happened.
    if (!error?.response) {
      throw {
        network: true,
        message: error?.message || "Network unreachable",
      };
    }
    if (error?.response?.data?._server_messages) {
      console.log("PARSED ERROR", parseErrorMessage(error?.response?.data));
      throw parseErrorMessage(error?.response?.data);
    }
    throw error?.response?.data;
  }
};
export const getData = async (endpoint, data) => {
  try {
    const response = await API.get(endpoint, data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

export const getNoticeListData = async (endpoint, params) => {
  try {
    const response = await RESOURCE_API.get(endpoint, { params });
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

export const getNoticeDetails = async (endpoint) => {
  try {
    const response = await API.get(endpoint);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

export const resetPassword = async (endpoint, params) => {
  try {
    const response = await API.get(endpoint, { params });
    return response.data;
  } catch (error) {
    throw error?.response?.data?.detail || error?.response?.data?.message || "Error Resetting Password";
  }
};

export const getCardsData = async (endpoint) => {
  try {
    const response = await API.get(endpoint);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

export const getNoticeSectionData = async (doctype) => {
  try {
    const response = await API.get(
      `/api/v1/income-tax/notice-sections?doctype=${doctype}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

export const createNotice = async (endpoint, data) => {
  try {
    const response = await RESOURCE_API.post(endpoint, data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

export const deleteDocument = async (endpoint) => {
  try {
    const response = await RESOURCE_API.delete(endpoint);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

export const deleteNotices = async (endpoint, data) => {
  try {
    const response = await API.post(endpoint, data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

export const deleteClients = async (endpoint, data) => {
  try {
    const response = await API.post(endpoint, data);
    return response.data;
  } catch (error) {
    console.error("ERRRRRR", error);
    throw error;
  }
};

export const saveNotice = async (endpoint, data) => {
  try {
    const response = await API.post(endpoint, data);
    return response.data;
  } catch (error) {
    if (error?.response?.data?._server_messages) {
      console.log("PARSED ERROR", parseErrorMessage(error?.response?.data));
      throw parseErrorMessage(error?.response?.data);
    }
    throw parseErrorMessage(error) ||
      error?.response?.data?.message?.message ||
      error?.response?.data?.message ||
      "Error posting data";
  }
};

export const saveClientDetails = async (endpoint, data) => {
  try {
    const response = await API.post(endpoint, data);
    return response.data;
  } catch (error) {
    const error_msg = parseErrorMessage(error);
    throw error_msg;
  }
};

export const postResourceData = async (endpoint, params) => {
  try {
    const response = await RESOURCE_API.get(endpoint, { params });
    return response.data;
  } catch (error) {
    if (error?.response?.data?._server_messages) {
      console.log("PARSED ERROR", parseErrorMessage(error?.response?.data));
      throw parseErrorMessage(error?.response?.data);
    }
    throw parseErrorMessage(error) ||
      error?.response?.data?.message?.message ||
      error?.response?.data?.message ||
      "Error posting data";
  }
};

// PUT Request
export const updateData = async (endpoint, data) => {
  try {
    const response = await API.put(endpoint, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error updating data";
  }
};

// PATCH Request (partial update; same error shape as updateData)
export const patchData = async (endpoint, data) => {
  try {
    const response = await API.patch(endpoint, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error updating data";
  }
};

// DELETE Request
export const deleteData = async (endpoint) => {
  try {
    const response = await API.delete(endpoint);
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error deleting data";
  }
};

// DELETE with a request body. Axios needs the body under `data` on a delete,
// which `deleteData` above does not pass. Used where the server requires a
// confirmation payload before destroying something (permanent partner delete).
export const deleteDataWithBody = async (endpoint, data) => {
  try {
    const response = await API.delete(endpoint, { data });
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error deleting data";
  }
};

// Structured-409 detector. The user-level-client-assignment work introduces
// two BE 409 shapes whose `detail` is an OBJECT (not a string and not a
// Pydantic-style array): `USER_HAS_ASSIGNMENTS` + `LAST_ADMIN_PROTECTED`.
// Existing helpers either flatten errors (`postData` → string) or pass them
// raw (`updateData`, `deleteData`, `postResponseData` → axios body). This
// helper accepts BOTH:
//   - the raw axios body (`error.response.data`) thrown by raw-throwing
//     helpers like `updateData` / `deleteData` / `postResponseData`, AND
//   - a full axios error if the caller is calling `API.<method>` directly
// and returns true iff the response is a 409 carrying the given code.
//
// Usage:
//   try { await updateData(USERS.TOGGLE_STATUS(id), {enabled: false}); }
//   catch (err) {
//     if (is409WithCode(err, "USER_HAS_ASSIGNMENTS")) { open modal }
//     else if (is409WithCode(err, "LAST_ADMIN_PROTECTED")) { toast }
//     else { generic-error toast }
//   }
//
// Why we don't just check status: callers don't have access to the response
// metadata after these wrappers throw. We sniff the body shape instead —
// `{detail: {error: <code>, ...}}` is unique to these structured 409s and
// won't false-positive on the standard FastAPI 422 (array) or string-detail
// 4xx responses.
export const is409WithCode = (err, code) => {
  if (!err || typeof code !== "string") return false;
  // Full axios error → drill into response.data.
  const body = err?.response?.data ?? err;
  const detail = body?.detail;
  return Boolean(
    detail &&
      typeof detail === "object" &&
      !Array.isArray(detail) &&
      detail.error === code
  );
};

// Read the structured-409 detail body when present. Returns the detail
// object ({error, message, counts?}) or null. Useful when the caller
// needs `counts` from `USER_HAS_ASSIGNMENTS` to populate the deactivation
// modal.
export const get409Detail = (err) => {
  const body = err?.response?.data ?? err;
  const detail = body?.detail;
  if (
    detail &&
    typeof detail === "object" &&
    !Array.isArray(detail) &&
    typeof detail.error === "string"
  ) {
    return detail;
  }
  return null;
};

export const importClientWrapper = async (endpoint, data) => {
  try {
    const response = await API.post(endpoint, data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

export const importClientWrapperGet = async (endpoint, params) => {
  try {
    const response = await API.get(endpoint, { params });
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

export const clientActivation = async (endpoint, data) => {
  try {
    const response = await RESOURCE_API.put(endpoint, data);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || error.response?.data?.message || "Error posting data";
  }
};

// Finalize Response API — FastAPI uses PUT with doctype_name/doc_name
export const finalizeResponse = async (doctype, name) => {
  try {
    const response = await API.put(AI.FINALIZE_RESPONSE, {
      doctype_name: doctype,
      doc_name: name,
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data?.detail || parseErrorMessage(error) || "Error finalizing response";
  }
};

// Update Response Status API — FastAPI uses PUT with doctype_name/doc_name/status
export const updateResponseStatus = async (doctype, name, responseStatus, editMode = false) => {
  try {
    const response = await API.put(AI.UPDATE_RESPONSE_STATUS, {
      doctype_name: doctype,
      doc_name: name,
      status: responseStatus,
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data?.detail || parseErrorMessage(error) || "Error updating response status";
  }
};
